import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Tooltip from '../ItemTooltip';
import Toast from '../../Shared/Toast';
import HoldButton from '../../Shared/HoldButton';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { getItemImageSrc, handleImgError } from '../../../utils/itemUtils';
import styles from './CraftingProgress.module.css';

interface CraftingProject {
  id: number;
  blueprintName: string;
  itemId: number;
  itemImg: string | null;
  itemCategory: string | null;
  knowledgeType: string | null;
  requiredRounds: number;
  remainingRounds: number;
  createdAt: string;
}

interface ResearchProject {
  id: number;
  blueprintName: string;
  itemId: number;
  itemImg: string | null;
  itemCategory: string | null;
  knowledgeType: string | null;
  totalResearchRounds: number;
  remainingResearchRounds: number;
  researchStartedAt: string;
}

interface Mercenary {
  id: number;
  professionSkillPoints: number;
  rounds: number;
}

const CraftingProgress: React.FC<{ mercenary: Mercenary; defaultTab?: 'crafting' | 'research' }> = ({ mercenary, defaultTab }) => {
  const { mercenary: ctxMercenary } = useMercenary();
  // Use live rounds from context so the cap reflects regeneration since page load
  const availableRounds = ctxMercenary?.rounds ?? mercenary.rounds;
  const [projects, setProjects] = useState<CraftingProject[]>([]);
  const [researchProjects, setResearchProjects] = useState<ResearchProject[]>([]);
  const [workRounds, setWorkRounds] = useState<{ [projectId: number]: number }>({});
  const [researchWorkRounds, setResearchWorkRounds] = useState<{ [projectId: number]: number }>({});
  const [sortConfig, setSortConfig] = useState<{ key: keyof CraftingProject; direction: 'asc' | 'desc' } | null>(null);
  const [tab, setTab] = useState<'crafting' | 'research'>(defaultTab || 'research');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);

  useEffect(() => {
    fetchCraftingProjects();
    fetchResearchProjects();
  }, []);

  // Refetch when switching to a tab, ensures data reflects any rounds spent
  // in the CraftingDistrict view before navigating here
  useEffect(() => {
    if (tab === 'research') fetchResearchProjects();
    if (tab === 'crafting') fetchCraftingProjects();
  }, [tab]);

  const fetchCraftingProjects = async () => {
    try {
      const { data } = await axios.get(`/api/craft/projects/${mercenary.id}`, { withCredentials: true });
      const sortedProjects = [...data.projects].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setProjects(sortedProjects);
    } catch (error) {
      displayToast('Error fetching crafting projects', 'error');
    }
  };

  const fetchResearchProjects = async () => {
    try {
      const { data } = await axios.get(`/api/craft/research/${mercenary.id}`, { withCredentials: true });
      setResearchProjects(data.researchProjects || []);
    } catch (error) {
      displayToast('Error fetching research projects', 'error');
    }
  };

  const handleWorkOnProject = async (projectId: number) => {
    const rounds = workRounds[projectId] || 1;
    try {
      const { data } = await axios.post(`/api/craft/projects/${projectId}/work`, { rounds, mercenaryId: mercenary.id }, { withCredentials: true });
      if (data.completed) {
        const qty = data.craftedQuantity && data.craftedQuantity > 1 ? ` ×${data.craftedQuantity}` : '';
        displayToast(`Crafting complete! ${data.craftedItemName}${qty} added to inventory.`, 'success');
      } else {
        displayToast('Work completed on crafting project!', 'success');
      }
      setWorkRounds((prev) => { const next = { ...prev }; delete next[projectId]; return next; });
      fetchCraftingProjects();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Error working on crafting project';
      displayToast(errorMsg, 'error');
    }
  };

  const handleWorkOnResearch = async (userBlueprintId: number) => {
    const rounds = researchWorkRounds[userBlueprintId] || 1;
    try {
      const { data } = await axios.post(`/api/craft/research/${userBlueprintId}/work`, { rounds, mercenaryId: mercenary.id }, { withCredentials: true });
      if (data.completed) {
        displayToast('Research complete! Blueprint unlocked.', 'success');
      } else {
        displayToast('Research progress updated.', 'success');
      }
      // Clear the rounds input after studying
      setResearchWorkRounds((prev) => { const next = { ...prev }; delete next[userBlueprintId]; return next; });
      fetchResearchProjects();
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Error working on research';
      displayToast(errorMsg, 'error');
      // Refetch so stale remainingResearchRounds gets corrected, prevents
      // the user getting stuck after spending rounds in the Crafting view first
      setResearchWorkRounds((prev) => { const next = { ...prev }; delete next[userBlueprintId]; return next; });
      fetchResearchProjects();
    }
  };

  const handleCancelResearch = async (userBlueprintId: number) => {
    try {
      await axios.delete(`/api/craft/research/${userBlueprintId}/cancel`, {
        data: { mercenaryId: mercenary.id },
        withCredentials: true,
      });
      setConfirmCancelId(null);
      displayToast('Research cancelled.', 'success');
      fetchResearchProjects();
    } catch (error: any) {
      setConfirmCancelId(null);
      displayToast(error.response?.data?.message || 'Failed to cancel research.', 'error');
    }
  };

  const handleRoundChange = (projectId: number, rounds: number) => {
    if (!rounds || rounds < 0) {
      setWorkRounds((prev) => ({ ...prev, [projectId]: 0 }));
      return;
    }
    const remaining = projects.find(p => p.id === projectId)?.remainingRounds || 1;
    const maxRounds = Math.min(remaining, availableRounds);
    setWorkRounds((prev) => ({ ...prev, [projectId]: Math.min(rounds, maxRounds) }));
  };

  const handleResearchRoundChange = (projectId: number, rounds: number) => {
    if (!rounds || rounds < 0) {
      setResearchWorkRounds((prev) => ({ ...prev, [projectId]: 0 }));
      return;
    }
    const remaining = researchProjects.find(p => p.id === projectId)?.remainingResearchRounds || 1;
    const maxRounds = Math.min(remaining, availableRounds);
    setResearchWorkRounds((prev) => ({ ...prev, [projectId]: Math.min(rounds, maxRounds) }));
  };

  const handleSort = (key: 'blueprintName' | 'requiredRounds' | 'remainingRounds' | 'createdAt') => {
    setSortConfig((prevSort) => {
      const direction = prevSort?.key === key && prevSort.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const sortedProjects = [...projects].sort((a, b) => {
    if (sortConfig) {
      const { key, direction } = sortConfig;
      const aVal = a[key] ?? '';
      const bVal = b[key] ?? '';
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    }
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  return (
    <div className={styles.craftingProgressContainer}>
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      <div className={styles.tabBar}>
        <button
          className={`${styles.tabButton} ${tab === 'research' ? styles.tabActive : ''}`}
          onClick={() => setTab('research')}
        >
          🔬 Research ({researchProjects.length})
        </button>
        <button
          className={`${styles.tabButton} ${tab === 'crafting' ? styles.tabActive : ''}`}
          onClick={() => setTab('crafting')}
        >
          ⚒️ Crafting ({projects.length})
        </button>
      </div>

      {tab === 'research' && (
        <>
          <h2>Ongoing Research</h2>
          {researchProjects.length === 0 ? (
            <p className={styles.noProjectsMessage}>No active research. Start studying a blueprint in the Crafting District.</p>
          ) : (
            <div className={styles.tableWrapper}>
            <table className={styles.projectTable}>
              <thead>
                <tr>
                  <th>Blueprint</th>
                  <th>Progress</th>
                  <th>Remaining</th>
                  <th>Rounds to Study</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {researchProjects.map((project) => {
                  const spent = project.totalResearchRounds - project.remainingResearchRounds;
                  const pct = Math.round((spent / project.totalResearchRounds) * 100);
                  return (
                    <tr key={project.id}>
                      <td data-label="Blueprint">
                        <Tooltip itemId={project.itemId}>
                          <div className={styles.itemCell}>
                            <span>{project.blueprintName}</span>
                          </div>
                        </Tooltip>
                      </td>
                      <td data-label="Progress">
                        <div className={styles.progressBarMini}>
                          <div className={styles.progressBarFill} style={{ width: `${pct}%` }} />
                          <span className={styles.progressBarLabel}>{pct}%</span>
                        </div>
                      </td>
                      <td data-label="Remaining">{project.remainingResearchRounds} / {project.totalResearchRounds}</td>
                      <td data-label="Rounds to Study">
                        <div className={styles.qtyControl}>
                        <HoldButton
                            onClick={() => setResearchWorkRounds((prev) => ({ ...prev, [project.id]: Math.max(1, (prev[project.id] || 1) - 1) }))}
                            className={styles.qtyBtn}
                          >-</HoldButton>
                          <input
                            type="number"
                            min="1"
                            max={Math.min(project.remainingResearchRounds, availableRounds)}
                            value={researchWorkRounds[project.id] || ''}
                            placeholder="1"
                            onChange={(e) => handleResearchRoundChange(project.id, e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
                            className={styles.roundInput}
                          />
                        <HoldButton
                            onClick={() => setResearchWorkRounds((prev) => ({ ...prev, [project.id]: Math.min(Math.min(project.remainingResearchRounds, availableRounds), (prev[project.id] || 1) + 1) }))}
                            className={styles.qtyBtn}
                          >+</HoldButton>
                        </div>
                      </td>
                      <td data-label="Action" className={styles.actionCell}>
                        <div className={styles.actionButtons}>
                        <button onClick={() => handleWorkOnResearch(project.id)} className={styles.workButton}>
                          Study
                        </button>
                        {confirmCancelId === project.id ? (
                          <>
                            <button onClick={() => handleCancelResearch(project.id)} className={styles.cancelButton}>
                              Confirm
                            </button>
                            <button onClick={() => setConfirmCancelId(null)} className={styles.workButton}>
                              No
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setConfirmCancelId(project.id)} className={styles.cancelButton}>
                            Cancel
                          </button>
                        )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}

      {tab === 'crafting' && (
        <>
          <h2>Ongoing Crafting Projects</h2>
          {projects.length === 0 ? (
            <p className={styles.noProjectsMessage}>No ongoing projects. Start a new crafting project in the Crafting District.</p>
          ) : (
            <div className={styles.tableWrapper}>
            <table className={styles.projectTable}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('blueprintName')}>Blueprint</th>
                  <th onClick={() => handleSort('requiredRounds')}>Required Rounds</th>
                  <th onClick={() => handleSort('remainingRounds')}>Remaining Rounds</th>
                  <th>Rounds to Work</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => (
                  <tr key={project.id}>
                    <td data-label="Blueprint">
                      <Tooltip itemId={project.itemId}>
                        <div className={styles.itemCell}>
                          <img
                            src={getItemImageSrc({ img: project.itemImg, itemCategory: project.itemCategory, knowledgeType: project.knowledgeType, name: project.blueprintName })}
                            alt={`${project.blueprintName} icon`}
                            className={styles.itemIcon}
                            onError={handleImgError}
                          />
                          <span>{project.blueprintName}</span>
                        </div>
                      </Tooltip>
                    </td>
                    <td data-label="Required">{project.requiredRounds}</td>
                    <td data-label="Remaining">{project.remainingRounds}</td>
                    <td data-label="Rounds to Work">
                      <div className={styles.qtyControl}>
                        <HoldButton
                          onClick={() => setWorkRounds((prev) => ({ ...prev, [project.id]: Math.max(1, (prev[project.id] || 1) - 1) }))}
                          className={styles.qtyBtn}
                        >-</HoldButton>
                        <input
                          type="number"
                          min="1"
                          max={Math.min(project.remainingRounds, mercenary.rounds)}
                          value={workRounds[project.id] || ''}
                          placeholder="1"
                          onChange={(e) => handleRoundChange(project.id, e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
                          className={styles.roundInput}
                        />
                        <HoldButton
                          onClick={() => setWorkRounds((prev) => ({ ...prev, [project.id]: Math.min(Math.min(project.remainingRounds, availableRounds), (prev[project.id] || 1) + 1) }))}
                          className={styles.qtyBtn}
                        >+</HoldButton>
                      </div>
                    </td>
                    <td data-label="Action">
                      <button onClick={() => handleWorkOnProject(project.id)} className={styles.workButton}>
                        Work on Project
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CraftingProgress;
